import { Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormProvider, useForm } from '@/components/react-hook-form';
import { useDPFormSuggestionsContext } from '@/modules/aiDP/context/dpFormSuggestions.context';
import { DPFormSuggestionsProvider } from '@/modules/aiDP/context/DPFormSuggestions.provider';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { useDispositiveDiagramSyncContext } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { RecipientDataProvider } from '@/modules/dispositiveProvisions/contexts/RecipientDataProvider';
import { CLIENT_OWNERSHIP_DESCRIPTION } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import { DispositiveProvisionsFormV2FormBody } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/DispositiveProvisionsFormV2FormBody';
import { DispositiveProvisionsFormV2ModalFooter } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/modal/DispositiveProvisionsFormV2ModalFooter';
import { DispositiveProvisionsFormV2ModalHeader } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/modal/DispositiveProvisionsFormV2ModalHeader';
import { InitialTileSelectionPanel } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/panels/InitialTileSelectionPanel';
import { DispositiveProvisionsFormV2Shape } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getInitialValuesV2 } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { useDiagramNodeSelectionHandler } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/hooks/useDiagramNodeSelectionHandler';
import { useSyncDiagram } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/hooks/useDiagramSyncContext';
import { usePrefetchRecipientDispositions } from '@/modules/dispositiveProvisions/hooks/usePrefetchRecipientDispositions';
import { useSubmitDispositiveProvisions } from '@/modules/dispositiveProvisions/hooks/useSubmitDispositiveProvisions';
import { MultiDocumentUploaderProvider } from '@/modules/documents/MultiDocumentUploader/context/MultiDocumentUploader.provider';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { EntityInEstateStatus } from '@/types/schema';

import { DispositiveProvisionsFormProps } from '../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { useSyncNonRootTargetDispositions } from '../hooks/useSyncNonRootTargetDispositions';
import { DispositionsUnsupportedPanel } from './components/panels/DispositionsUnsupportedPanel';

interface DispositiveProvisionsFormV2Props
  extends DispositiveProvisionsFormProps {
  onSwitchTargetContextAfterSubmit: (selectedTargetId: string) => void;
  survivingSpouseRecipientId?: string | null;
}

function DispositiveProvisionsFormV2Inner({
  onClose,
  headerRef,
  footerRef,
  onAfterSubmit,
  onDidSave,
  onSwitchTargetContextAfterSubmit,
  firstDeathClientId,
  survivingSpouseRecipientId,
  onTestamentaryEntitySectionSubmit,
}: DispositiveProvisionsFormV2Props) {
  const canEditDP = useCanEditDispositiveProvisions();
  const ctx = useDispositiveProvisionsContext();
  const rootCtx = ctx.rootTargetContext ?? ctx;

  const { formVariant } = ctx;

  const { entityDisplayName: rootEntityDisplayName } = rootCtx;

  const { primaryClients } = useHouseholdDetailsContext();
  const { oppositeDeathOrderClientId } = useDPFormSuggestionsContext();

  // Submission logic extracted to custom hook
  const {
    onSubmit,
    handleEditEntitySplitScreenSubmission,
    handleTestamentaryEntitySplitScreenSubmission,
    handleDiagramTileClickSubmission,
    isSubmitting,
  } = useSubmitDispositiveProvisions({
    onAfterSubmit: onAfterSubmit,
    onClose,
    onDidSave,
    onTestamentaryEntitySectionSubmit,
  });

  // Callback to switch to the opposite death order
  const handleSwitchDeathOrder = useCallback(() => {
    if (!oppositeDeathOrderClientId || !primaryClients) return;

    // Determine which section to switch to based on whether the opposite
    // death order client is the first or second primary client
    const isFirstPrimaryClient =
      primaryClients[0]?.id === oppositeDeathOrderClientId;
    const section = isFirstPrimaryClient
      ? EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1
      : EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2;

    handleEditEntitySplitScreenSubmission(section);
  }, [
    handleEditEntitySplitScreenSubmission,
    oppositeDeathOrderClientId,
    primaryClients,
  ]);

  const hasRelevantDispositionScenarios = useMemo(() => {
    if (!ctx.dispositionScenarios || !firstDeathClientId) return false;
    return ctx.dispositionScenarios.some(
      (scenario) => scenario.firstGrantorDeath?.id === firstDeathClientId
    );
  }, [ctx.dispositionScenarios, firstDeathClientId]);

  // Show warning overlay for second-death recipient tiles
  const { selectedNode } = useDispositiveDiagramSyncContext();

  const shouldShowInitialTileSelectionPanel =
    !selectedNode && !hasRelevantDispositionScenarios;

  // Sync diagram context with form context
  useSyncDiagram();

  // Sync the second death dispositions for a nested target
  useSyncNonRootTargetDispositions({
    survivingSpouseRecipientId: survivingSpouseRecipientId ?? null,
  });

  useDiagramNodeSelectionHandler({
    currentTargetId: ctx.id,
    isTwoClientHousehold: ctx.isTwoClientHousehold,
    isSubmitting,
    handleDiagramTileClickSubmission,
    onSwitchDispositionTargetAfterSubmit: onSwitchTargetContextAfterSubmit,
  });

  return (
    <HouseholdPermissionAwareDisabledProvider forceDisabled={!canEditDP}>
      <Stack
        data-testid="DispositiveProvisionsForm"
        component="form"
        height="100%"
        onSubmit={onSubmit}
        noValidate
      >
        <Stack sx={{ height: '100%', position: 'relative' }}>
          <Stack flexGrow={1} sx={{ overflowY: 'auto' }}>
            <DispositiveProvisionsFormV2ModalHeader
              formVariant={formVariant}
              headerRef={headerRef}
              headerText={
                rootCtx.isClientProfile
                  ? CLIENT_OWNERSHIP_DESCRIPTION
                  : rootEntityDisplayName
              }
              suggestionsTargetId={
                rootCtx.clientProfileOrEntityOrTestamentaryEntityId
              }
              workflowTrackerTargetId={
                rootCtx.isClientProfile
                  ? null
                  : rootCtx.clientProfileOrEntityOrTestamentaryEntityId
              }
              handleEditEntitySplitScreenOnSubmit={
                handleEditEntitySplitScreenSubmission
              }
              selectedFirstDeathClientId={firstDeathClientId}
              handleTestamentaryEntitySplitScreenOnSubmit={
                handleTestamentaryEntitySplitScreenSubmission
              }
            />
            <DispositiveProvisionsFormV2FormBody
              onSwitchDeathOrder={handleSwitchDeathOrder}
            />
          </Stack>
          {selectedNode && <DispositionsUnsupportedPanel />}
          {shouldShowInitialTileSelectionPanel && <InitialTileSelectionPanel />}
        </Stack>
        <DispositiveProvisionsFormV2ModalFooter
          formVariant={formVariant}
          footerRef={footerRef}
          handleEditEntitySplitScreenOnSubmit={
            handleEditEntitySplitScreenSubmission
          }
          onClose={onClose}
          isSubmitting={isSubmitting}
          onSubmit={onSubmit}
        />
      </Stack>
    </HouseholdPermissionAwareDisabledProvider>
  );
}

/**
 * DispositiveProvisionsFormV2 is an enhancement to the current
 * DispositiveProvisionsForm that will allow users to manage DP Recipients
 * for both grantor death events, and interact with the entity diagram on
 * the left-hand side of the SplitScreenModal.
 */
export function DispositiveProvisionsFormV2(
  props: DispositiveProvisionsFormV2Props
) {
  const {
    primaryClients,
    isTwoClientHousehold,
    dispositionScenarios,
    isClientProfile,
    clientProfileOrEntityOrTestamentaryEntityId,
    isTestamentaryEntity,
    estateStatus,
  } = useDispositiveProvisionsContext();

  const primaryClientIds = useMemo(() => {
    return primaryClients.map((c) => c.id);
  }, [primaryClients]);

  const formMethods = useForm<DispositiveProvisionsFormV2Shape>({
    defaultValues: getInitialValuesV2({
      primaryClientIds,
      firstDeathPrimaryClientId: props.firstDeathClientId,
      dispositionScenarios,
      isTwoClientHousehold,
      clientProfileOrEntityOrTestamentaryEntityId,
      isClientProfile,
      isTestamentaryEntity,
      isInEstate: estateStatus === EntityInEstateStatus.InEstate,
    }),
  });

  const [firstDeathRecipients, secondDeathRecipients] = useWatch({
    control: formMethods.control,
    name: [
      'dispositiveProvisionsV2.firstDeath.recipients',
      'dispositiveProvisionsV2.secondDeath.recipients',
    ] as const,
  });

  const survivingSpouseRecipientId =
    primaryClientIds.find((cId) => cId !== props.firstDeathClientId) ?? null;

  // Prefetch dispositions for recipient entities that can distribute before
  // the final diagram section.
  usePrefetchRecipientDispositions({
    firstDeathRecipients: firstDeathRecipients,
    secondDeathRecipients,
    firstGrantorDeathId: props.firstDeathClientId ?? null,
    survivingSpouseRecipientId,
  });

  return (
    <FormProvider {...formMethods}>
      <RecipientDataProvider>
        <MultiDocumentUploaderProvider>
          <DPFormSuggestionsProvider>
            <DispositiveProvisionsFormV2Inner
              {...props}
              survivingSpouseRecipientId={survivingSpouseRecipientId}
            />
          </DPFormSuggestionsProvider>
        </MultiDocumentUploaderProvider>
      </RecipientDataProvider>
    </FormProvider>
  );
}
